import React from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Box } from '@mui/material';
import FinmapleComparator from '../components/Comparator/FinmapleComparator';
import NewsletterSection from '../components/Layout/NewsletterSection';
import SEOHead from '../components/SEO/SEOHead';
import PageHeader from '../components/Layout/PageHeader';
import AtomicAnswer from '../components/SEO/AtomicAnswer';
import FAQSection from '../components/SEO/FAQSection';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FinancialService',
  name: 'Guía de Cuentas Registradas: TFSA, RRSP, FHSA, RESP - FinMaple',
  description: 'Aprende sobre las cuentas con beneficios fiscales en Canadá: TFSA, RRSP, FHSA y RESP. Guía para newcomers.',
  provider: { '@type': 'Organization', name: 'FinMaple', url: 'https://www.finmaple.com' },
  dateModified: '2026-02-01',
};

const faqs = [
  {
    question: '¿Cuál es la primera cuenta registrada que debo abrir como newcomer?',
    answer: 'TFSA (CELI) es la prioridad #1. Es 100% libre de impuestos (ganancias, dividendos, retiros). Como newcomer, tu room empieza desde el año que llegas a Canadá. Si planeas comprar casa, abre también FHSA - te da doble beneficio: deducción + retiro tax-free.',
  },
  {
    question: '¿Qué es la diferencia entre TFSA y RRSP?',
    answer: 'TFSA: contribuyes con dinero ya gravado, crece y se retira 100% tax-free. RRSP: contribución reduce tus impuestos hoy (deducción), pero pagas impuestos al retirar en el futuro. Para newcomers con income bajo el primer año, TFSA es mejor. RRSP es mejor cuando ganas más.',
  },
  {
    question: '¿Tengo room de TFSA desde 2009 si acabo de llegar?',
    answer: 'No. Tu contribution room de TFSA empieza SOLO desde el año en que te conviertes en residente fiscal de Canadá. Si llegaste en 2025, tienes $7,000 de room para 2025 + $7,000 para 2026. No acumulas room por años anteriores.',
  },
  {
    question: '¿Qué es FHSA y por qué es mejor que RRSP para comprar casa?',
    answer: 'FHSA (First Home Savings Account) te da DOBLE beneficio: deducción fiscal al contribuir (como RRSP) + retiro 100% tax-free para tu primera casa (como TFSA). A diferencia del RRSP Home Buyers\' Plan, NO tienes que repagar lo que retiras. Límite: $8K/año, $40K lifetime.',
  },
  {
    question: '¿Cuánto debo contribuir al RESP de mis hijos?',
    answer: 'Contribuye mínimo $2,500 por hijo al año para maximizar el government match (CESG): el gobierno agrega 20% = $500/año gratis por hijo. Es dinero literalmente regalado. Máximo lifetime: $50,000 por beneficiario.',
  },
];

const CuentasRegistradasPage = () => {
  const { i18n } = useTranslation();
  return (
  <Box sx={{ bgcolor: 'neutral.50' }}>
    <SEOHead
      seoKey="registeredAccounts"
      title="Cuentas Registradas en Canadá: TFSA, RRSP, FHSA, RESP - Guía Newcomers"
      description="Guía completa de cuentas con beneficios fiscales para newcomers: TFSA ($7K/año), RRSP (deducción), FHSA ($8K para tu casa), RESP (dinero gratis del gobierno)."
      canonical="/cuentas-registradas"
      routeKey="registeredAccounts"
      schema={schema}
      noIndex={i18n.language !== 'es'}
    />
    <PageHeader
      variant="minimal"
      title="Cuentas registradas en Canadá: TFSA, RRSP, FHSA, RESP"
      subtitle="Aprende cuál abrir primero como newcomer y cómo maximizar tus beneficios fiscales."
      breadcrumbs={[{ label: 'Cuentas Registradas' }]}
      maxWidth="lg"
    />
    <Container maxWidth="lg" sx={{ mb: 2 }}>
      <AtomicAnswer text="Como newcomer, abre primero un TFSA (ahorro 100% tax-free, $7K/año). Si planeas comprar casa, abre FHSA ($8K/año, deducción + retiro tax-free). RRSP es mejor cuando tu ingreso sube. Si tienes hijos, RESP es obligatorio: el gobierno regala 20% match ($500/año/hijo)." />
    </Container>
    <Container maxWidth="lg">
      <FinmapleComparator category="registered_accounts" />
      <FAQSection faqs={faqs} />
    </Container>
    <NewsletterSection />
  </Box>
  );
};

export default CuentasRegistradasPage;
